

import { generatePath, Link, UIMatch, useLocation, useMatches } from 'react-router-dom';
import { Crumb } from '../../route';
import { connect } from 'react-redux';
import { RootState } from '../../store/store';
import React, { useMemo } from 'react';
import { IValueWithTime } from '../../types/channel-types';

type ComponentProps = ReturnType<typeof mapState>;

const SEPARATOR = '/';

const Breadcrumb: React.FC<ComponentProps> = ({ store }) => {
  const matches = useMatches();
  const location = useLocation();

  const getElement = (path: string, text: string, isLast: boolean) => {
    return (
      <span key={path} className="flex items-center">
        <Link className={`hover:text-red ${isLast ? 'text-primary' : ''}`} to={path}>
          {text}
        </Link>
        <span className="mx-1">{SEPARATOR}</span>
      </span>
    )
  }

  const getBreadcrumbItems = (matches: UIMatch<unknown, unknown>[]) => {
    return matches.flatMap((match) => {
      if (!match?.handle) {
        return [];
      }

      const { handle } = match as { handle: { crumb?: () => Crumb } };
      const { crumb: crumbCallback } = handle;
      const crumbObject = crumbCallback?.();

      if (crumbObject?.isSkip) {
        return [];
      }

      const crumbText = crumbObject?.text;
      if (crumbText) {
        return getElement(match.pathname, crumbText, location.pathname === match.pathname)
      }

      const crumbs = crumbObject?.segments;

      if (crumbs.length !== 0) {
        const entitiesNames: JSX.Element[] = [];

        crumbs.forEach((entity) => {
          if (entity.isStaticPage) {
            const path = generatePath(entity.path, match.params);
            const isLast = location.pathname === path;
            const element = getElement(path, entity.pageName, isLast);
            entitiesNames.push(element);
            return;
          }

          const storeEntity = store[entity.name];
          const singularEntityData = storeEntity?.[entity.singularEntityName];

          if (singularEntityData) {
            const path = generatePath(entity.path, match.params);


            let fieldData: string | IValueWithTime<string> | undefined = singularEntityData[entity.fieldName];
            if (fieldData && typeof fieldData !== 'string') {
              fieldData = (fieldData as IValueWithTime<string>).value;
            }

            if (!fieldData && entity.secondFieldName) {
              fieldData = singularEntityData[entity.secondFieldName];
            }

            const isLast = location.pathname === path;

            const element = getElement(path, fieldData, isLast);
            entitiesNames.push(element);
            return;
          }
        });

        return entitiesNames;
      }

      return [];
    });
  };

  const getEntityLength = (matches: UIMatch<unknown, unknown>[]): number => {
    let length = 0;

    for (const match of matches) {
      if (!match?.handle) {
        continue;
      }

      const { handle } = match as { handle: { crumb?: () => Crumb } };
      const { crumb: crumbCallback } = handle;
      const crumbObject = crumbCallback?.();

      if (crumbObject?.isSkip) {
        continue;
      }

      if (crumbObject?.segments) {
        length = length + crumbObject.segments.length;
        break;
      }

      length++;
    };

    return length;
  }

  const listData = useMemo(() => getBreadcrumbItems(matches), [matches, location.pathname, store]);
  const length = useMemo(() => getEntityLength(matches), [matches]);

  const isViewBreadcrumbs = (length && listData.length === length);

  return (
    <div className={`c-breadcrumb flex flex-row flex-nowrap text-nowrap items-center mb-3 overflow-x-auto min-h-10 ${isViewBreadcrumbs ? '' : 'opacity-0'}`}>
      {listData}
    </div>
  );
};

const mapState = (state: RootState) => ({
  store: state,
});

export default connect(mapState, null)(Breadcrumb);
